import { integrationRegistry } from "@p0security/shared/integrations/registry";
import {
  IntegrationResourceRule,
  RequestorRule,
  ResourceRule,
} from "@p0security/shared/types/policy/types";
import { widetype } from "@p0security/util/collections";
import { kebabCase, mapValues, sortBy, upperFirst } from "lodash";

export const YAML_RULE_SEARCH_PARAM = "yaml";
export const DEFAULT_TAB_SEARCH_PARAM = "defaultTab";

export const DEFAULT_NEW_REQUESTOR: RequestorRule = { type: "any" };
export const DEFAULT_NEW_RESOURCE: ResourceRule = { type: "any" };

/** Hidden from the policy resource picker until the bundle request flow is ready in UI */
export const policyResourcePickerHiddenServices: ReadonlySet<
  IntegrationResourceRule["service"]
> = new Set(["bundle"]);

const idToLabel = (id: string) => upperFirst(kebabCase(id).replace(/-/g, " "));

const requestableRegistry = widetype.pickBy(
  integrationRegistry,
  (reg) => reg.requestType === "requestable"
);

export const filterTypeOptionsByResource = widetype.mapValues(
  requestableRegistry,
  (reg) =>
    mapValues(reg?.filters ?? {}, ({ label }, id) => label ?? idToLabel(id))
);

export const accessTypeOptionsByResource = mapValues(
  requestableRegistry,
  (reg) => Object.keys(reg?.accesses ?? {})
);

/** Default human-readable labels for filter key values.
 *
 * A filter's `labeler` in the integration registry only needs to specify a key
 * when its label differs from this map and from the {@link labelForFilterKey}
 * fallback. For example `id` maps to "ID" everywhere, so it never needs a
 * `labeler` entry.
 */
export const defaultKeyLabels: Record<string, string> = {
  id: "ID",
  "full-resource-name": "Full resource name",
};

/** Resolves the human-readable label for a filter key.
 *
 * Precedence: the integration's per-filter `labeler`, then
 * {@link defaultKeyLabels}, then a generated title-case fallback.
 */
export const labelForFilterKey = (
  key: string,
  labeler?: Record<string, string>
): string => labeler?.[key] ?? defaultKeyLabels[key] ?? idToLabel(key);

export type ResourceFilterType<K extends IntegrationResourceRule["service"]> =
  keyof Extract<IntegrationResourceRule, { service: K }>["filters"];

type FilterKeyOptions = Array<{ label: string; value: string }>;
export type FilterKeyOptionsByType =
  | { readonly type: "boolean" }
  | { readonly type: "input" }
  | { readonly type: "options"; readonly options: FilterKeyOptions };

/** Maps a single registry filter to the policy editor's key options,
 * applying {@link labelForFilterKey} to each key value.
 */
const toFilterKeyOptions = (filter: {
  keys: readonly string[] | "boolean" | "string";
  labeler?: Record<string, string>;
}): FilterKeyOptionsByType =>
  filter.keys === "boolean"
    ? { type: "boolean" }
    : filter.keys === "string"
      ? { type: "input" }
      : {
          type: "options",
          options: sortBy(
            filter.keys.map((key) => ({
              value: key,
              label: labelForFilterKey(key, filter.labeler),
            })),
            "label"
          ),
        };

/** Filter key options for each integration, derived from the integration
 * registry's filter schema (applying {@link labelForFilterKey}).
 */
export const filterKeyOptionsByResource = mapValues(
  requestableRegistry,
  (reg) => mapValues(reg?.filters ?? {}, toFilterKeyOptions)
);
